<script lang="ts">
  /**
   * Shared Tooltip Component
   *
   * Usage:
   * <Tooltip text="Tooltip content">
   *   <button>Hover me</button>
   * </Tooltip>
   *
   * With icon and size variant:
   * <Tooltip text="Tooltip content" size="sm">
   *   <Info size={16} />
   * </Tooltip>
   *
   * Different positions:
   * <Tooltip text="Tooltip content" position="left" size="sm">
   *   <span>Hover</span>
   * </Tooltip>
   */

  type Props = {
    text: string;
    position?: 'top' | 'bottom' | 'left' | 'right';
    size?: 'sm' | 'md';
    delay?: number;
    children?: any;
  };

  let { text, position = 'top', size = 'md', delay = 0, children }: Props = $props();

  let showTooltip = $state(false);
  let tooltipPos = $state({ x: 0, y: 0 });
  let timeoutId: number | null = null;

  function handleMouseEnter(event: MouseEvent) {
    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();

    // Calculate position based on preferred position
    let x = rect.left + rect.width / 2;
    let y = rect.top;

    if (position === 'bottom') {
      y = rect.bottom;
    } else if (position === 'left') {
      x = rect.left;
      y = rect.top + rect.height / 2;
    } else if (position === 'right') {
      x = rect.right;
      y = rect.top + rect.height / 2;
    }

    tooltipPos = { x, y };

    if (delay > 0) {
      timeoutId = window.setTimeout(() => {
        showTooltip = true;
      }, delay);
    } else {
      showTooltip = true;
    }
  }

  function handleMouseLeave() {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    showTooltip = false;
  }
</script>

<span
  class="tooltip-wrapper"
  role="presentation"
  onmouseenter={handleMouseEnter}
  onmouseleave={handleMouseLeave}
>
  {@render children()}
</span>

{#if showTooltip}
  <div
    class="tooltip-portal {position} {size}"
    role="tooltip"
    aria-live="polite"
    style="top: {tooltipPos.y}px; left: {tooltipPos.x}px;"
  >
    {text}
  </div>
{/if}

<style>
  .tooltip-wrapper {
    display: inline-flex;
    align-items: center;
    cursor: help;
    line-height: 0;
  }

  .tooltip-portal {
    position: fixed;
    background: var(--color-text-primary);
    color: var(--color-background);
    border-radius: var(--radius-md);
    white-space: nowrap;
    z-index: 10000;
    pointer-events: none;
    box-shadow: var(--shadow-lg);
  }

  .tooltip-portal.md {
    padding: var(--spacing-md) var(--spacing-lg);
    font-size: var(--font-size-sm);
  }

  .tooltip-portal.sm {
    padding: var(--spacing-xs) var(--spacing-sm);
    font-size: var(--font-size-xs);
  }

  /* Position-specific transforms and arrows */
  .tooltip-portal.top {
    transform: translate(-50%, calc(-100% - 14px));
  }

  .tooltip-portal.top::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-top-color: var(--color-text-primary);
  }

  .tooltip-portal.bottom {
    transform: translate(-50%, 10px);
  }

  .tooltip-portal.bottom::after {
    content: '';
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-bottom-color: var(--color-text-primary);
  }

  .tooltip-portal.left {
    transform: translate(calc(-100% - 10px), -50%);
  }

  .tooltip-portal.left::after {
    content: '';
    position: absolute;
    left: 100%;
    top: 50%;
    transform: translateY(-50%);
    border: 5px solid transparent;
    border-left-color: var(--color-text-primary);
  }

  .tooltip-portal.right {
    transform: translate(10px, -50%);
  }

  .tooltip-portal.right::after {
    content: '';
    position: absolute;
    right: 100%;
    top: 50%;
    transform: translateY(-50%);
    border: 5px solid transparent;
    border-right-color: var(--color-text-primary);
  }
</style>
